import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  containerVariants,
  titleVariants,
  textVariants,
  buttonVariants,
  imageVariants,
  floatingVariants,
} from "../utils/animations";
import portraitImage from "../assets/portrait.webp";

// Hero Section Component
const HeroSection = () => {
  const { t } = useTranslation();

  // Smooth scroll function
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      scrollToSection("contact");
    }
  };

  return (
    <section
      className="relative min-h-screen bg-gradient-to-br from-[#0F1B2D] to-[#1a2b42] text-white overflow-hidden"
      aria-labelledby="hero-title"
    >
      <div className="absolute inset-0 bg-black/20" aria-hidden="true"></div>
      <motion.div
        className="relative z-10 container mx-auto px-6 py-20 flex flex-col lg:flex-row items-center min-h-screen mt-8 lg:mt-0"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="lg:w-1/2 mb-12 lg:mb-0">
          <motion.h1
            id="hero-title"
            className="font-playfair text-5xl lg:text-7xl font-bold mb-6 leading-tight"
          >
            <motion.span
              className="block text-[#C5A66A]"
              variants={titleVariants}
            >
              {t("hero.title.line1")}
            </motion.span>
            <motion.span className="block" variants={titleVariants}>
              {t("hero.title.line2")}
            </motion.span>
            <motion.span className="block" variants={titleVariants}>
              {t("hero.title.line3")}
            </motion.span>
          </motion.h1>
          <motion.p
            className="text-xl lg:text-2xl mb-8 text-gray-300 leading-relaxed"
            variants={textVariants}
          >
            {t("hero.subtitle")}
          </motion.p>
          <motion.div
            className="flex flex-col sm:flex-row gap-4"
            variants={textVariants}
          >
            <motion.button
              className="bg-[#C5A66A] text-[#0F1B2D] px-8 py-4 rounded-lg font-semibold text-lg hover:bg-[#b8966b] transition-colors duration-300"
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
              onClick={() => scrollToSection("contact")}
              onKeyDown={handleKeyDown}
              aria-label="Book strategy session - navigate to contact section"
            >
              {t("hero.cta")}
            </motion.button>
          </motion.div>
        </div>
        <div className="lg:w-1/2 flex justify-center">
          <div className="relative">
            <motion.div
              className="relative z-2 w-90 h-90 lg:w-116 lg:h-116 rounded-full bg-gradient-to-br from-[#C5A66A] to-[#d4b886] p-2"
              variants={imageVariants}
              aria-hidden="true"
            >
              <img
                src={portraitImage}
                loading="eager"
                fetchPriority="high"
                alt="Daaniyal Khan - Strategieberater Portrait"
                className="w-full h-full rounded-full object-cover object-[center_5%]"
              />
            </motion.div>
            <motion.div
              className="absolute z-1 -top-4 -right-4 w-24 h-24 bg-[#C5A66A] rounded-full opacity-20"
              variants={floatingVariants}
              animate="float"
              aria-hidden="true"
            ></motion.div>
            <motion.div
              className="absolute -bottom-4 -left-4 w-16 h-16 bg-white rounded-full opacity-10"
              variants={floatingVariants}
              animate="float"
              style={{ animationDelay: "1.5s" }}
              aria-hidden="true"
            ></motion.div>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
